/**
 * Pay runs — types and calls.
 *
 * Everything printed comes from the run's own snapshot (`pay_run_rows`,
 * `pay_run_days`, `pay_run_advances`), never recomputed from live timesheets. A
 * payslip handed over last month must still print the same figures after a
 * salary is edited.
 */

import { request, uploadBlob } from './api';

/** EPF/ETF members are paid monthly, everyone else weekly (migration 0014). */
export type PayCycle = 'weekly' | 'monthly';

export interface PrepareDay {
  workDate: string;
  roleName: string | null;
  payBasis: 'day' | 'trip';
  hoursTenths: number;
  otTenths: number;
  salaryRs: number;
  otRs: number;
  allowanceRs: number;
  sundayAllowanceRs: number;
  allowanceReason: string | null;
  earnedRs: number;
}

export interface PrepareAdvance {
  id: string;
  workDate: string;
  amountRs: number;
  note: string | null;
}

export interface PrepareLoan {
  loanId: string;
  givenOn: string;
  loanAmountRs: number;
  reason: string;
  amountRs: number;
  balanceAfterRs: number;
}

export interface PrepareRow {
  workerId: string;
  workerName: string;
  rolesLabel: string;
  epfNo: string | null;
  isEpfMember: boolean;
  daysWorked: number;
  otTenths: number;
  grossRs: number;
  allowanceRs: number;
  baseEarnedRs: number;
  /** What EPF/ETF was worked out on: monthly, the base earned capped at his EPF salary. */
  epfBasisRs: number;
  epfSalaryRs: number | null;
  advancesRs: number;
  epfEmployeeRs: number;
  epfEmployerRs: number;
  etfRs: number;
  netRs: number;
  /** Short from his last run, taken off before loans. */
  carriedInRs: number;
  /** Taken off his loans this run. What he is handed is takeHomeRs. */
  loanRs: number;
  /** Below zero: he is short that much, carried to his next run. */
  takeHomeRs: number;
  loans: PrepareLoan[];
  /** Non-null means Blocked: he has an unpaid approved day before the From date. */
  blockedSince: string | null;
  days: PrepareDay[];
  advances: PrepareAdvance[];
}

export interface PrepareView {
  from: string;
  to: string;
  cycle: PayCycle;
  /** The usual pay day: the pay-day setting for a monthly run, the Saturday for a weekly one. */
  payOn: string | null;
  rows: PrepareRow[];
  unapprovedDays: string[];
  alreadyPaid: { workerName: string; days: number }[];
  rates: {
    epfEmployeePctX100: number;
    epfEmployerPctX100: number;
    etfPctX100: number;
  };
}

export interface RunSummary {
  id: string;
  period_from: string;
  period_to: string;
  status: 'unapproved' | 'approved';
  worker_count: number;
  gross_rs: number;
  advances_rs: number;
  base_earned_rs: number;
  epf_employee_rs: number;
  epf_employer_rs: number;
  etf_rs: number;
  /** Taken off loans in this run. */
  loan_rs: number;
  /** Cash needed to pay the employees — after loans. */
  net_rs: number;
  created_at: string;
  approved_at: string | null;
  created_by_name: string;
  cycle: PayCycle;
  pay_on: string | null;
}

/** Who prepared and approved a run — stamped on the pack with their signatures. */
export interface RunPeople {
  created_by: string;
  approved_by: string | null;
  approved_by_name: string | null;
}

export interface RunRow {
  id: string;
  worker_id: string;
  worker_name: string;
  roles_label: string;
  is_epf_member: number;
  epf_no: string | null;
  days_worked: number;
  ot_tenths: number;
  gross_rs: number;
  allowance_rs: number;
  base_earned_rs: number;
  advances_rs: number;
  epf_employee_rs: number;
  epf_employer_rs: number;
  etf_rs: number;
  /** Before loans: gross − advances − EPF. */
  net_rs: number;
  /** Short from his last run, taken off before loans. */
  carried_in_rs: number;
  loan_rs: number;
  /** What he is handed: net pay less the shortfall brought in and the loan.
   *  Below zero he is handed nothing and is short that much — see shortRs. */
  take_home_rs: number;
  /** What EPF/ETF was worked out on, and the EPF salary it was capped at. */
  epf_basis_rs: number;
  epf_salary_rs: number | null;
  /** 1 once he has signed for his pay on the tablet. */
  signed: number;
  signed_at: string | null;
  sort_order: number;
}

export interface RunDay {
  worker_id: string;
  work_date: string;
  role_name: string | null;
  pay_basis: 'day' | 'trip' | null;
  hours_tenths: number;
  ot_tenths: number;
  salary_rs: number;
  ot_rs: number;
  allowance_rs: number;
  sunday_allowance_rs: number;
  allowance_reason: string | null;
  gross_rs: number;
  base_earned_rs: number;
  advance_rs: number;
}

export interface RunAdvance {
  advance_id: string;
  worker_id: string;
  worker_name: string;
  work_date: string;
  amount_rs: number;
  note: string | null;
  pay_week_ending: string;
  signed_at: string | null;
  signed: number;
  paid_by_name: string;
  role_name: string | null;
  week_rs: number;
  /** Who approved that day's labour — "Approved" on the receipt. */
  approved_by: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
}

export interface RunLoan {
  loan_id: string;
  worker_id: string;
  amount_rs: number;
  balance_after_rs: number;
  given_on: string;
  loan_amount_rs: number;
  reason: string;
}

export interface RunDetail {
  run: RunSummary & RunPeople & {
    epf_employee_pct_x100: number;
    epf_employer_pct_x100: number;
    etf_pct_x100: number;
  };
  rows: RunRow[];
  days: RunDay[];
  advances: RunAdvance[];
  loans: RunLoan[];
}

/**
 * A take-home below zero is not paid as a minus: he is handed nothing, and
 * what is short is carried to his next run (17 October 2026).
 */
export const handedRs = (takeHomeRs: number): number => Math.max(0, takeHomeRs);
export const shortRs = (takeHomeRs: number): number => Math.max(0, -takeHomeRs);

export const payrunApi = {
  /** Defaults to the most recently completed Sunday-to-Saturday week. */
  /** Weekly defaults to the last completed week; monthly to the last completed month. */
  prepare: (cycle: PayCycle, from?: string, to?: string) => {
    const query = new URLSearchParams({ cycle });
    if (from && to) { query.set('from', from); query.set('to', to); }
    return request<PrepareView>(`/payruns/prepare?${query.toString()}`);
  },

  /** payOn moves the pay day off the usual one — for a public holiday. */
  create: (cycle: PayCycle, from: string, to: string, workerIds: string[], payOn?: string) =>
    request<{ id: string; from: string; to: string; status: string; workerCount: number; netRs: number }>(
      '/payruns',
      { method: 'POST', body: { cycle, from, to, workerIds, ...(payOn ? { payOn } : {}) } },
    ),

  list: () => request<{ runs: RunSummary[] }>('/payruns'),

  get: (id: string) => request<RunDetail>(`/payruns/${id}`),

  /** Owner only. Also accrues the EPF/ETF for the run. */
  approve: (id: string) =>
    request<{ id: string; status: string; approvedAt: string }>(`/payruns/${id}/approve`, {
      method: 'POST',
    }),

  unapprove: (id: string) =>
    request<{ id: string; status: string }>(`/payruns/${id}/approve`, { method: 'DELETE' }),

  remove: (id: string) => request<void>(`/payruns/${id}`, { method: 'DELETE' }),

  /** He signs for his pay once the run is approved: a PNG, straight to R2. */
  signPayslip: (id: string, workerId: string, png: Blob) =>
    uploadBlob<{ workerId: string; signed: boolean; signedAt: string }>(
      `/payruns/${id}/rows/${workerId}/signature`, png, 'image/png',
    ),

  /** A path for SignatureImage, which fetches it with the session token. */
  // ?v= changes with each signing (see loanApi.signaturePath).
  payslipSignaturePath: (id: string, workerId: string, signedAt?: string | null) =>
    `/payruns/${id}/rows/${workerId}/signature${signedAt ? `?v=${encodeURIComponent(signedAt)}` : ''}`,
};
